import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip
} from "@chakra-ui/react";
import { FiCheckCircle, FiInfo } from "react-icons/fi";
import { StateStatsEvent, StateProps } from "../../classes/state-machine/state";
import { StatesMachine } from "../../classes/state-machine/state-machine";
import { SUPPORTS_MULTIPLE_EXECUTORS } from "../../node-catalog";
import { useEffect, useMemo, useState } from "react";
import { apiPipeline } from "api/api-pipeline";
import { apiJWT } from "api/api-jwt";
import { ButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { createLinkAndDownload } from "utils/create-link-and-download";
import { CenarioValue } from "./EscolherCenariosModal";
import { Toast } from "components/toast";

interface DiagnosticPanelProps {
  selectedStateId: number;
  pipelineData: StatesMachine;
  operation: StateProps["operation"];
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}

interface ErrorDiagnostic {
  title: string;
  reasons: string[];
  impact: string;
  renderAction?: (
    event: StateStatsEvent,
    pipelineData: StatesMachine,
    updatePipelineData: (
      updater: (draft: StatesMachine) => StatesMachine | void
    ) => void,
    selectedStateId: number
  ) => React.ReactNode;
}

interface SuccessDiagnosticColors {
  bg: string;
  border: string;
  badgeScheme: string;
  title: string;
  label: string;
  text: string;
}

interface SuccessDiagnostic {
  title: string;
  reasons: string[];
  impact: string;
  colors?: SuccessDiagnosticColors;
  /** When set, this entry is hidden and its total is merged into the given primary code. */
  mergedInto?: number;
  renderAction?: (
    event: StateStatsEvent,
    pipelineData: StatesMachine,
    updatePipelineData: (
      updater: (draft: StatesMachine) => StatesMachine | void
    ) => void,
    selectedStateId: number
  ) => React.ReactNode;
}

type ErrorDetailItem = {
  code: number | null;
  statusDescription: string | null;
  total: number;
};

interface ErrorDetailsAccordionProps {
  stateId: number;
  errorCode: number;
}

type DiagnosticStateStatsEvent = StateStatsEvent & {
  sourceStateId: number;
};

interface DiagnosticStateStats {
  finished: boolean;
  events: DiagnosticStateStatsEvent[];
}

function ErrorDetailsAccordion({
  stateId,
  errorCode
}: ErrorDetailsAccordionProps) {
  const [items, setItems] = useState<ErrorDetailItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || hasLoaded) return;

    setIsLoading(true);
    setError(null);

    apiPipeline
      .get<ErrorDetailItem[]>(
        `/pipeline-new/state/${stateId}?errorCode=${errorCode}`
      )
      .then(({ data }) => setItems(data ?? []))
      .catch(() => setError("Não foi possível carregar os detalhes."))
      .finally(() => {
        setIsLoading(false);
        setHasLoaded(true);
      });
  }, [errorCode, hasLoaded, isOpen, stateId]);

  return (
    <Accordion
      allowToggle
      onChange={(index) => setIsOpen(index === 0)}
      mt="8px"
      reduceMotion
    >
      <AccordionItem border="none">
        <AccordionButton px="0" py="4px" minH="unset">
          <Text
            fontSize="xs"
            fontWeight="600"
            color="#9B2C2C"
            flex="1"
            textAlign="left"
          >
            Ver detalhes do erro
          </Text>
          <AccordionIcon color="#9B2C2C" />
        </AccordionButton>
        <AccordionPanel px="0" pt="6px" pb="2px">
          {isLoading && (
            <Flex align="center" justify="center" py={4}>
              <Spinner size="sm" color="red.400" />
            </Flex>
          )}

          {!isLoading && error && (
            <Text color="red.500" fontSize="xs" py={2} textAlign="center">
              {error}
            </Text>
          )}

          {!isLoading && !error && items.length === 0 && (
            <Text color="gray.500" fontSize="xs" py={2} textAlign="center">
              Nenhum detalhe encontrado.
            </Text>
          )}

          {!isLoading && !error && items.length > 0 && (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th fontSize="10px" px="8px">
                    Descrição
                  </Th>
                  <Th isNumeric fontSize="10px" px="8px">
                    Código
                  </Th>
                  <Th isNumeric fontSize="10px" px="8px">
                    Total
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((item, i) => (
                  <Tr key={i}>
                    <Td px="8px" py="6px">
                      <Text fontSize="xs">{item.statusDescription ?? "—"}</Text>
                    </Td>
                    <Td isNumeric px="8px" py="6px">
                      {item.code != null ? (
                        <Badge
                          colorScheme={item.code >= 400 ? "red" : "green"}
                          fontSize="10px"
                        >
                          {item.code}
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          —
                        </Text>
                      )}
                    </Td>
                    <Td isNumeric px="8px" py="6px">
                      <Badge colorScheme="blue" fontSize="10px">
                        {item.total}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

const ERROR_DIAGNOSTICS: Record<number, ErrorDiagnostic> = {
  400: {
    title: "Requisição inválida",
    reasons: [
      "Dados obrigatórios ausentes ou em formato incorreto",
      "Parâmetros de configuração do nó inválidos"
    ],
    impact:
      "Os leads afetados não foram processados e ficaram retidos neste nó."
  },
  401: {
    title: "Erro de autenticação — Usuário banco",
    reasons: [
      "Credenciais do usuário do banco expiradas ou inválidas",
      "Senha do usuário foi alterada externamente"
    ],
    impact:
      "Nenhum lead pôde ser processado. A digitação está bloqueada até a correção das credenciais."
  },
  403: {
    title: "Acesso negado — Usuário banco",
    reasons: [
      "Usuário não tem permissão para esta operação no banco",
      "Conta do banco suspensa ou bloqueada"
    ],
    impact:
      "O processamento está impedido. Corrija as credenciais para desbloquear o fluxo."
  },
  404: {
    title: "Recurso não encontrado",
    reasons: [
      "O convênio, tabela ou configuração referenciada não existe mais",
      "O endpoint do banco pode estar indisponível"
    ],
    impact:
      "Os leads não foram processados. Verifique se as configurações do nó ainda são válidas."
  },
  408: {
    title: "Timeout na requisição",
    reasons: [
      "O serviço externo demorou demais para responder",
      "Instabilidade na conexão com o banco ou API"
    ],
    impact:
      "Os leads afetados podem ser reprocessados. Verifique a estabilidade do serviço."
  },
  410: {
    title: "Erro de validação — Dados bancários (CSV)",
    reasons: [
      "O arquivo CSV contém dados bancários em formato inválido",
      "Campos obrigatórios de dados bancários ausentes no CSV"
    ],
    impact:
      "Os leads com dados bancários inválidos não puderam ser digitados. Corrija o CSV e reenvie."
  },
  411: {
    title: "Erro de validação — Endereço (CSV)",
    reasons: [
      "O arquivo CSV contém dados de endereço em formato inválido",
      "Campos obrigatórios de endereço ausentes no CSV"
    ],
    impact:
      "Os leads com endereço inválido não puderam ser digitados. Corrija o CSV e reenvie."
  },
  422: {
    title: "Dados inválidos para processamento",
    reasons: [
      "Os dados do lead não atendem aos critérios do banco",
      "Valores fora do intervalo permitido pela tabela selecionada"
    ],
    impact: "Os leads rejeitados não avançam no fluxo."
  },
  429: {
    title: "Limite de requisições excedido",
    reasons: [
      "O volume de processamento excedeu o limite da API do banco",
      "Rate limit atingido — muitas requisições simultâneas"
    ],
    impact:
      "O processamento foi temporariamente bloqueado. Os leads serão reprocessados automaticamente."
  },
  500: {
    title: "Erro interno do servidor",
    reasons: [
      "Falha inesperada no serviço de processamento",
      "Bug ou indisponibilidade momentânea do sistema"
    ],
    impact:
      "Os leads afetados ficaram retidos. Se o erro persistir, entre em contato com o suporte."
  },
  502: {
    title: "Erro de gateway",
    reasons: [
      "O servidor intermediário não conseguiu se comunicar com o banco",
      "Serviço externo fora do ar"
    ],
    impact:
      "O processamento está temporariamente indisponível. Aguarde e tente novamente."
  },
  503: {
    title: "Serviço indisponível",
    reasons: [
      "O serviço do banco está em manutenção ou fora do ar",
      "Sobrecarga no servidor externo"
    ],
    impact:
      "Os leads não podem ser processados enquanto o serviço não voltar. Aguarde a normalização."
  },
  600: {
    title: "Base Estagnada",
    reasons: [
      "Não há manobra viável no curto prazo. Recomendado verificar fila de acompanhamento."
    ],
    impact: ""
  }
};

function getDefaultDiagnostic(
  code: number,
  statusDescription: string
): ErrorDiagnostic {
  return {
    title: statusDescription || `Erro ${code}`,
    reasons: ["Erro inesperado retornado pelo serviço de processamento"],
    impact:
      "Os leads afetados ficaram retidos neste nó e não avançaram no fluxo."
  };
}

export async function downloadPerfisCsv(
  idMailing: number | undefined,
  cenarios: CenarioValue[],
  selectedStateId: number
): Promise<void> {
  const queryParams = new URLSearchParams();
  cenarios.forEach((c) => queryParams.append("perfis", c));
  const response = await apiJWT.get(
    `/mailing-lead/pipeline/${idMailing}/download/perfil?${queryParams.toString()}&idStateIA=${selectedStateId}`
  );
  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  createLinkAndDownload(
    blob,
    `perfis-nao-selecionados-pipeline-${idMailing}.csv`
  );
}

export const SUCCESS_DIAGNOSTICS: Record<number, SuccessDiagnostic> = {
  602: {
    title: "Refinanciamento",
    reasons: [],
    impact: "",
    colors: {
      bg: "rgba(250, 245, 255, 0.6)",
      border: "purple.200",
      badgeScheme: "purple",
      title: "#553C9A",
      label: "#44337A",
      text: "#44337A"
    },
    renderAction: (_, pipelineData, _updatePipelineData, selectedStateId) => (
      <ButtonWithLoading
        w="100%"
        variant="outline"
        onClick={async () => {
          try {
            await downloadPerfisCsv(
              pipelineData.idMailing,
              [
                CenarioValue.REFINANCIAMENTO_COM_TROCO,
                CenarioValue.REFINANCIAMENTO_REDUCAO_COM_TROCO
              ],
              selectedStateId
            );
            Toast({ title: "CSV baixado com sucesso", status: "success" });
          } catch (err) {
            Toast({ title: "Erro ao baixar CSV", status: "error" });
          }
        }}
      >
        Baixar CSV
      </ButtonWithLoading>
    )
  },
  605: {
    title: "Refinanciamento com Troco com Redução",
    reasons: [],
    impact: "",
    mergedInto: 602,
    colors: {
      bg: "rgba(250, 245, 255, 0.6)",
      border: "purple.200",
      badgeScheme: "purple",
      title: "#553C9A",
      label: "#44337A",
      text: "#44337A"
    }
  },
  601: {
    title: "Portabilidade",
    reasons: [
      "Portabilidade de 1,85% para 1,55%",
      "Novo prazo de 108x",
      "Redução estimada de parcela",
      "Sem liberação de troco"
    ],
    impact: "",
    colors: {
      bg: "rgba(235, 248, 255, 0.6)",
      border: "blue.200",
      badgeScheme: "blue",
      title: "#2B6CB0",
      label: "#2C5282",
      text: "#2C5282"
    },
    renderAction: (_, pipelineData, _updatePipelineData, selectedStateId) => {
      return (
        <ButtonWithLoading
          w="100%"
          variant="outline"
          onClick={async () => {
            try {
              await downloadPerfisCsv(
                pipelineData.idMailing,
                [CenarioValue.PORTABILIDADE_PURA],
                selectedStateId
              );
              Toast({ title: "CSV baixado com sucesso", status: "success" });
            } catch (err) {
              Toast({ title: "Erro ao baixar CSV", status: "error" });
            }
          }}
        >
          Baixar CSV
        </ButtonWithLoading>
      );
    }
  },
  603: {
    title: "Contrato Novo",
    reasons: ["Oportunidade de entrante com margem de 40%"],
    impact: "",
    colors: {
      bg: "rgba(230, 255, 250, 0.6)",
      border: "teal.200",
      badgeScheme: "teal",
      title: "#285E61",
      label: "#234E52",
      text: "#234E52"
    },
    renderAction: (_, pipelineData, _updatePipelineData, selectedStateId) => (
      <ButtonWithLoading
        w="100%"
        variant="outline"
        onClick={async () => {
          try {
            await downloadPerfisCsv(
              pipelineData.idMailing,
              [CenarioValue.CONTRATO_NOVO],
              selectedStateId
            );
            Toast({ title: "CSV baixado com sucesso", status: "success" });
          } catch (err) {
            Toast({ title: "Erro ao baixar CSV", status: "error" });
          }
        }}
      >
        Baixar CSV
      </ButtonWithLoading>
    )
  },
  604: {
    title: "CPFs inconclusivos",
    reasons: [
      "Os leads afetados não atendem aos critérios de elegibilidade e não puderam ser processados."
    ],
    impact: "",
    colors: {
      bg: "rgba(241, 241, 245, 0.6)",
      border: "gray.300",
      badgeScheme: "gray",
      title: "#3D3D4E",
      label: "#52525B",
      text: "#52525B"
    }
    // renderAction: (_, pipelineData, _updatePipelineData, selectedStateId) => (
    //   <ButtonWithLoading
    //     w="100%"
    //     variant="outline"
    //     onClick={async () => {
    //       try {
    //         await downloadPerfisCsv(
    //           pipelineData.idMailing,
    //           [CenarioValue.INCONCLUSIVO],
    //           selectedStateId
    //         );
    //         Toast({ title: "CSV baixado com sucesso", status: "success" });
    //       } catch (err) {
    //         Toast({ title: "Erro ao baixar CSV", status: "error" });
    //       }
    //     }}
    //   >
    //     Baixar CSV
    //   </ButtonWithLoading>
    // )
  }
};

function getDefaultSuccessDiagnostic(
  code: number,
  statusDescription: string
): SuccessDiagnostic {
  return {
    title: statusDescription || `Sucesso ${code}`,
    reasons: [],
    impact: ""
  };
}

function getFlowImpact(
  operation: StateProps["operation"],
  errorTotal: number,
  successTotal: number
): string | null {
  const total = errorTotal + successTotal;
  if (total === 0) return null;

  const errorPercentage = Math.round((errorTotal / total) * 100);

  if (errorPercentage === 100) {
    return "Todos os leads estão sem oportunidades neste nó. Nenhum lead avançou para os próximos passos do pipeline.";
  }

  if (errorPercentage >= 50) {
    return `${errorPercentage}% dos leads estão sem oportunidades. Uma parte significativa do fluxo foi comprometida — apenas ${successTotal} de ${total} leads avançaram.`;
  }

  return `${errorPercentage}% dos leads estão sem oportunidades (${errorTotal} de ${total}). A maioria avançou normalmente no fluxo.`;
}

export function getStateStats(
  stateId: number,
  operation: StateProps["operation"],
  pipelineData: StatesMachine
): DiagnosticStateStats | undefined {
  const state = pipelineData.getState(stateId);
  if (!state) return undefined;

  if (
    SUPPORTS_MULTIPLE_EXECUTORS.includes(operation) &&
    state.stateType === "NOOP"
  ) {
    const relatedStates = pipelineData.states.filter(
      (candidate) =>
        candidate.operation === operation && candidate.stateType === "EXECUTOR"
    );

    const eventsMap = new Map<string, DiagnosticStateStatsEvent>();
    let finished = false;

    relatedStates.forEach((relatedState) => {
      const stats = pipelineData.statusByState.get(relatedState.id);
      if (!stats) return;

      finished = finished || stats.finished;

      stats.events.forEach((event) => {
        // Keeps each event tied to the EXECUTOR state that produced it.
        const key = `${relatedState.id}-${event.code}-${event.statusDescription}`;
        const current = eventsMap.get(key);
        if (!current) {
          eventsMap.set(key, {
            ...event,
            sourceStateId: relatedState.id
          });
          return;
        }

        eventsMap.set(key, {
          ...current,
          total: current.total + event.total
        });
      });
    });

    if (!eventsMap.size) return undefined;

    return {
      finished,
      events: [...eventsMap.values()]
    };
  }

  const stats = pipelineData.statusByState.get(stateId);
  if (!stats) return undefined;

  return {
    ...stats,
    events: stats.events.map((event) => ({
      ...event,
      sourceStateId: stateId
    }))
  };
}

export function DiagnosticPanel({
  selectedStateId,
  pipelineData,
  operation,
  updatePipelineData
}: DiagnosticPanelProps) {
  const stats = useMemo(
    () => getStateStats(selectedStateId, operation, pipelineData),
    [selectedStateId, operation, pipelineData]
  );

  const successCodes = useMemo(
    () => Object.keys(SUCCESS_DIAGNOSTICS).map(Number),
    []
  );

  const errorEvents = useMemo(
    () =>
      (stats?.events ?? []).filter((e) => {
        const code = e.code ?? 400;
        return code >= 400 && !successCodes.includes(code);
      }),
    [stats, successCodes]
  );

  const successTotal = useMemo(
    () =>
      (stats?.events ?? [])
        .filter((e) => {
          const code = e.code ?? 400;
          return (code >= 200 && code < 400) || successCodes.includes(code);
        })
        .reduce((sum, e) => sum + e.total, 0),
    [stats, successCodes]
  );

  const successEvents = useMemo(() => {
    const groupMap = new Map<number, DiagnosticStateStatsEvent>();

    (stats?.events ?? []).forEach((e) => {
      const code = e.code ?? -1;
      if (!successCodes.includes(code)) return;
      const canonicalCode = SUCCESS_DIAGNOSTICS[code]?.mergedInto ?? code;
      const existing = groupMap.get(canonicalCode);
      if (existing) {
        groupMap.set(canonicalCode, {
          ...existing,
          total: existing.total + e.total
        });
      } else {
        groupMap.set(canonicalCode, { ...e, code: canonicalCode });
      }
    });

    return [...groupMap.values()].sort(
      (a, b) =>
        successCodes.indexOf(a.code ?? -1) - successCodes.indexOf(b.code ?? -1)
    );
  }, [stats, successCodes]);

  const errorTotal = useMemo(
    () => errorEvents.reduce((sum, e) => sum + e.total, 0),
    [errorEvents]
  );

  if (!errorEvents.length && !successEvents.length) return null;

  const flowImpact = getFlowImpact(operation, errorTotal, successTotal);

  return (
    <Box mt="16px">
      {/* Success details */}
      {successEvents.length > 0 && (
        <>
          <Flex align="center" gap="6px" mb="10px">
            <Icon as={FiCheckCircle} color="#276749" fontSize="16px" />
            <Text fontWeight="700" fontSize="sm" color="#276749">
              Resultados de sucesso
            </Text>
          </Flex>
          <Flex flexDir="column" gap="10px" mb="12px">
            {successEvents.map((event, i) => {
              const diagnostic =
                SUCCESS_DIAGNOSTICS[event.code ?? -1] ??
                getDefaultSuccessDiagnostic(
                  event.code ?? -1,
                  event.statusDescription
                );

              const sc = diagnostic.colors;
              const successBg = sc?.bg ?? "rgba(240, 255, 244, 0.6)";
              const successBorder = sc?.border ?? "green.100";
              const successBadge = sc?.badgeScheme ?? "green";
              const successTitle = sc?.title ?? "#276749";
              const successLabel = sc?.label ?? "#276749";
              const successText = sc?.text ?? "#22543D";

              return (
                <Box
                  key={i}
                  p="10px 12px"
                  borderRadius="10px"
                  bg={successBg}
                  border="1px solid"
                  borderColor={successBorder}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    mb={
                      diagnostic.reasons.length > 0 || diagnostic.impact
                        ? "6px"
                        : undefined
                    }
                  >
                    <Flex align="center" gap="6px">
                      <Tooltip label={event.statusDescription}>
                        <Text
                          fontWeight="700"
                          fontSize="xs"
                          color={successTitle}
                          maxW="180px"
                          isTruncated
                        >
                          {diagnostic.title}
                        </Text>
                      </Tooltip>
                    </Flex>
                    <Badge
                      colorScheme={successBadge}
                      variant="subtle"
                      fontSize="xs"
                    >
                      {event.total} lead{event.total !== 1 ? "s" : ""}
                    </Badge>
                  </Flex>

                  {diagnostic.reasons.length > 0 && (
                    <Box mb="6px">
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={successLabel}
                        mb="2px"
                      >
                        Detalhes:
                      </Text>
                      {diagnostic.reasons.map((reason, j) => (
                        <HStack
                          key={j}
                          align="flex-start"
                          spacing="6px"
                          ml="4px"
                        >
                          <Text
                            fontSize="xs"
                            color={successLabel}
                            lineHeight="1.5"
                          >
                            •
                          </Text>
                          <Text
                            fontSize="xs"
                            color={successText}
                            lineHeight="1.5"
                          >
                            {reason}
                          </Text>
                        </HStack>
                      ))}
                    </Box>
                  )}

                  {diagnostic.impact && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={successLabel}
                        mb="2px"
                      >
                        Impacto:
                      </Text>
                      <Text
                        fontSize="xs"
                        color={successText}
                        lineHeight="1.5"
                        ml="4px"
                      >
                        {diagnostic.impact}
                      </Text>
                    </Box>
                  )}

                  {diagnostic.renderAction && (
                    <Box mt="8px">
                      {diagnostic.renderAction(
                        event,
                        pipelineData,
                        updatePipelineData,
                        selectedStateId
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Flex>
        </>
      )}
      {/* <Flex align="center" gap="6px" mb="10px">
        <Icon as={FiAlertTriangle} color="#E53E3E" fontSize="16px" />
        <Text fontWeight="700" fontSize="sm" color="#E53E3E">
          Diagnósticos
        </Text>
      </Flex>

      Summary
      <Alert
        status="error"
        borderRadius="10px"
        mb="12px"
        py="8px"
        px="12px"
        fontSize="sm"
        bg="rgba(255, 193, 193, 0.6)"
      >
        <AlertIcon boxSize="16px" />
        <Text>
          <Text as="span" fontWeight="700">
            {errorTotal}
          </Text>{" "}
          lead{errorTotal !== 1 ? "s" : ""} com erro
          {errorTotal + successTotal > 0 && (
            <>
              {" "}
              de{" "}
              <Text as="span" fontWeight="700">
                {errorTotal + successTotal}
              </Text>{" "}
              processado{errorTotal + successTotal !== 1 ? "s" : ""}
            </>
          )}
        </Text>
      </Alert> */}
      {/* Error details */}
      <Flex flexDir="column" gap="10px" mb="12px">
        {errorEvents.map((event, i) => {
          const diagnostic =
            ERROR_DIAGNOSTICS[event.code ?? 400] ??
            getDefaultDiagnostic(event.code ?? 400, event.statusDescription);

          return (
            <Box
              key={i}
              p="10px 12px"
              borderRadius="10px"
              bg="rgba(255, 245, 245, 0.6)"
              border="1px solid"
              borderColor="red.100"
            >
              <Flex justify="space-between" align="center" mb="6px">
                <Flex align="center" gap="6px">
                  <Tooltip label={event.statusDescription}>
                    <Text
                      fontWeight="700"
                      fontSize="xs"
                      color="#C53030"
                      maxW="180px"
                      isTruncated
                    >
                      {diagnostic.title}
                    </Text>
                  </Tooltip>
                </Flex>
                <Badge colorScheme="red" variant="subtle" fontSize="xs">
                  {event.total} lead{event.total !== 1 ? "s" : ""}
                </Badge>
              </Flex>

              <Box mb="6px">
                <Text fontSize="xs" fontWeight="600" color="#9B2C2C" mb="2px">
                  Possíveis causas:
                </Text>
                {diagnostic.reasons.map((reason, j) => (
                  <HStack key={j} align="flex-start" spacing="6px" ml="4px">
                    <Text fontSize="xs" color="#9B2C2C" lineHeight="1.5">
                      •
                    </Text>
                    <Text fontSize="xs" color="#742A2A" lineHeight="1.5">
                      {reason}
                    </Text>
                  </HStack>
                ))}
              </Box>

              <Box>
                <Text fontSize="xs" fontWeight="600" color="#9B2C2C" mb="2px">
                  Impacto:
                </Text>
                <Text fontSize="xs" color="#742A2A" lineHeight="1.5" ml="4px">
                  {diagnostic.impact}
                </Text>
              </Box>

              <ErrorDetailsAccordion
                stateId={event.sourceStateId ?? selectedStateId}
                errorCode={event.code ?? 400}
              />

              {diagnostic.renderAction && (
                <Box mt="8px">
                  {diagnostic.renderAction(
                    event,
                    pipelineData,
                    updatePipelineData,
                    selectedStateId
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Flex>

      {/* Overall flow impact */}
      {flowImpact && (
        <Box
          p="10px 12px"
          borderRadius="10px"
          bg="rgba(255, 255, 220, 0.6)"
          border="1px solid"
          borderColor="yellow.200"
        >
          <Flex align="center" gap="6px" mb="4px">
            <Icon as={FiInfo} color="#975A16" fontSize="14px" />
            <Text fontWeight="700" fontSize="xs" color="#975A16">
              Impacto no fluxo
            </Text>
          </Flex>
          <Text fontSize="xs" color="#744210" lineHeight="1.5">
            {flowImpact}
          </Text>
        </Box>
      )}
    </Box>
  );
}
